import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError, fileUrl, formatDateTime } from "@/lib/api";
import { Loading, EmptyState } from "@/components/States";
import { toast } from "sonner";
import ReservasView from "./ReservasView";
import PublicationsView from "../shared/Publications";
import PullToRefresh from "@/components/PullToRefresh";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, Tooltip } from "recharts";
import {
  Users2, CalendarDays, FileText, Receipt, PieChart as PieIcon, AlertTriangle, Award,
  Package, Vote, Copy, Download, Check, Clock,
} from "lucide-react";

const CATS = [
  { key: "encomendas", label: "Encomendas", icon: Package },
  { key: "reservas", label: "Reservas", icon: CalendarDays },
  { key: "enquetes", label: "Enquetes", icon: Vote },
  { key: "assembleias", label: "Assembleias", icon: Users2 },
  { key: "boletos", label: "Boletos", icon: Receipt },
  { key: "balancete", label: "Balancetes", icon: PieIcon },
  { key: "multas", label: "Multas", icon: AlertTriangle },
  { key: "certidoes", label: "Certidões", icon: Award },
  { key: "regimento", label: "Regimento", icon: FileText },
  { key: "manuais", label: "Manuais", icon: FileText },
];

const CHART_COLORS = ["#B026FF", "#FF2079", "#8A2BE2", "#00E5FF", "#34C759", "#FFCC00", "#9D4EDD", "#FF6B9D"];
const brl = (n) => Number(n).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function Section({ children }) { return <div className="space-y-3">{children}</div>; }

function Encomendas({ pkgId }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["packages"], queryFn: async () => (await api.get("/packages")).data });

  const doPickup = async (id) => {
    await api.post(`/packages/${id}/pickup`);
    qc.invalidateQueries({ queryKey: ["packages"] });
    qc.invalidateQueries({ queryKey: ["home"] });
    qc.invalidateQueries({ queryKey: ["notif-count"] });
    qc.invalidateQueries({ queryKey: ["services-summary"] });
    setConfirm(false);
    toast.success("Encomenda retirada. ✅");
  };

  if (isLoading) return <Loading />;

  if (pkgId) {
    const p = (data || []).find((x) => x.id === pkgId);
    if (!p) return <EmptyState icon={Package} title="Encomenda não encontrada" subtitle="Ela pode já ter sido retirada." />;
    const pending = p.status === "aguardando";
    return (
      <Section>
        <div className="glass rounded-3xl p-6" data-testid="package-detail">
          <div className="flex items-center gap-3">
            <span className="text-3xl">📦</span>
            <div className="flex-1 min-w-0">
              <p className="font-display font-semibold">{p.description}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Recebida em {formatDateTime(p.event_at || p.created_at)}</p>
              {p.location && <p className="text-xs text-muted-foreground">Retirada: {p.location}</p>}
            </div>
          </div>
          <div className={`mt-4 inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full ${pending ? "bg-yellow-500/15 text-yellow-400" : "bg-emerald-500/15 text-emerald-400"}`}>
            {pending ? "📦 Aguardando retirada" : <><Check className="w-3 h-3" /> Retirada em {formatDateTime(p.picked_up_at || p.picked_at)}</>}
          </div>
          {pending && (
            <button onClick={() => setConfirm(true)} data-testid="package-pickup-cta" className="mt-5 w-full rounded-full brand-gradient py-3 text-white font-semibold active:scale-95 transition-transform">Confirmar retirada</button>
          )}
        </div>
        {confirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-5">
            <div className="absolute inset-0 bg-black/70" onClick={() => setConfirm(false)} />
            <div className="relative w-full max-w-xs glass-strong rounded-3xl border border-white/10 p-6 float-in text-center" data-testid="pickup-confirm">
              <p className="font-display font-semibold">Confirmar retirada?</p>
              <p className="text-sm text-muted-foreground mt-1">Confirmar que esta encomenda foi retirada?</p>
              <div className="mt-5 flex gap-2">
                <button onClick={() => setConfirm(false)} className="flex-1 rounded-full bg-white/5 border border-white/10 py-2.5 text-sm font-medium">Cancelar</button>
                <button onClick={() => doPickup(p.id)} data-testid="pickup-confirm-btn" className="flex-1 rounded-full brand-gradient py-2.5 text-white text-sm font-semibold">Confirmar retirada</button>
              </div>
            </div>
          </div>
        )}
      </Section>
    );
  }

  if (!data.length) return <EmptyState icon={Package} title="Nenhuma encomenda" subtitle="Você será avisado quando algo chegar." />;
  return (
    <Section>
      {data.map((p) => (
        <button key={p.id} onClick={() => nav(`/app/servicos?cat=encomendas&pkg=${p.id}`)} data-testid="package-item" className="w-full text-left glass rounded-3xl p-4 flex items-center gap-3 card-hover">
          <span className="text-2xl">📦</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">{p.description}</p>
            <p className="text-xs text-muted-foreground">{formatDateTime(p.event_at || p.created_at)}</p>
          </div>
          {p.status === "aguardando" ? (
            <span className="text-xs px-3 py-1.5 rounded-full bg-yellow-500/15 text-yellow-400 font-semibold">Aguardando</span>
          ) : (
            <span className="text-xs text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> Retirada</span>
          )}
        </button>
      ))}
    </Section>
  );
}

function Reservas() {
  return <ReservasView />;
}

function Enquetes({ focusId }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["polls"], queryFn: async () => (await api.get("/polls")).data });
  const vote = async (pid, option) => { try { await api.post(`/polls/${pid}/vote`, { option }); qc.invalidateQueries({ queryKey: ["polls"] }); toast.success("Voto registrado!"); } catch (e) { toast.error(apiError(e)); } };
  if (isLoading) return <Loading />;
  if (!data.length) return <EmptyState icon={Vote} title="Nenhuma enquete" />;
  return (
    <Section>
      {data.map((p) => (
        <div key={p.id} data-testid="poll-item" className={`glass rounded-3xl p-4 ${focusId === p.id ? "ring-2 ring-primary/60" : ""}`}>
          <p className="font-display font-semibold text-sm mb-3">🗳️ {p.question}</p>
          <div className="space-y-2">
            {p.options.map((opt) => {
              const count = p.counts[opt] || 0;
              const pct = p.total_votes ? Math.round((count / p.total_votes) * 100) : 0;
              const voted = p.voted === opt;
              return (
                <button key={opt} onClick={() => vote(p.id, opt)} data-testid="poll-option" className="w-full relative rounded-xl overflow-hidden border border-white/10 text-left">
                  <div className="absolute inset-y-0 left-0 brand-gradient opacity-30" style={{ width: `${pct}%` }} />
                  <div className="relative flex items-center justify-between px-4 py-2.5 text-sm">
                    <span className={voted ? "font-semibold text-primary" : ""}>{voted && "✓ "}{opt}</span>
                    <span className="text-xs text-muted-foreground">{pct}%</span>
                  </div>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-2">{p.total_votes} voto(s)</p>
        </div>
      ))}
    </Section>
  );
}

function Assembleias() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["assemblies"], queryFn: async () => (await api.get("/assemblies")).data });
  const attend = async (id) => { try { await api.post(`/assemblies/${id}/attend`); qc.invalidateQueries({ queryKey: ["assemblies"] }); } catch (e) { toast.error(apiError(e)); } };
  if (isLoading) return <Loading />;
  if (!data.length) return <EmptyState icon={Users2} title="Nenhuma assembleia" />;
  return (
    <Section>
      {data.map((a) => (
        <div key={a.id} data-testid="assembly-item" className="glass rounded-3xl p-4 card-hover">
          <div className="flex items-center justify-between">
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${a.status === "futura" ? "bg-primary/15 text-primary" : "bg-white/5 text-muted-foreground"}`}>{a.status === "futura" ? "Futura" : "Anterior"}</span>
            <span className="text-xs text-muted-foreground">{new Date(a.date).toLocaleDateString("pt-BR")} · {a.time}</span>
          </div>
          <p className="font-display font-semibold text-sm mt-2">{a.title}</p>
          <p className="text-xs text-muted-foreground mt-1">{a.agenda}</p>
          <p className="text-xs text-muted-foreground mt-2">👥 {a.attendance_count || 0} presença(s) registrada(s)</p>
          <div className="flex gap-2 mt-3">
            <button onClick={() => attend(a.id)} data-testid="assembly-attend" className={`flex-1 rounded-full py-2 text-xs font-semibold transition-colors ${a.attended ? "bg-emerald-500/15 text-emerald-400" : "brand-gradient text-white"}`}>
              {a.attended ? "✓ Estive presente" : "Estive presente"}
            </button>
            {a.ata_url && <a href={fileUrl(a.ata_url)} target="_blank" rel="noreferrer" data-testid="assembly-ata" className="rounded-full bg-white/5 border border-white/10 px-4 py-2 text-xs font-medium flex items-center gap-1"><Download className="w-3.5 h-3.5" /> Ata</a>}
          </div>
        </div>
      ))}
    </Section>
  );
}

function Documentos() {
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["documents", q], queryFn: async () => (await api.get("/documents", { params: { q } })).data });
  return (
    <Section>
      <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="doc-search" placeholder="Buscar documento..." className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-primary/50" />
      {isLoading ? <Loading /> : !data.length ? <EmptyState icon={FileText} title="Nenhum documento" /> :
        data.map((d) => (
          <div key={d.id} data-testid="document-item" className="glass rounded-2xl p-4 flex items-center gap-3 card-hover">
            <FileText className="w-5 h-5 text-primary" />
            <div className="flex-1"><p className="text-sm font-semibold">{d.title}</p><p className="text-xs text-muted-foreground">{d.category}</p></div>
          </div>
        ))}
    </Section>
  );
}

function Boletos({ focusId }) {
  const { data, isLoading } = useQuery({ queryKey: ["bills"], queryFn: async () => (await api.get("/bills")).data });
  const STATUS = { pago: "text-emerald-400 bg-emerald-500/10", pendente: "text-yellow-400 bg-yellow-500/10", vencido: "text-red-400 bg-red-500/10" };
  const copy = (code) => { navigator.clipboard?.writeText(code); toast.success("Código copiado!"); };
  if (isLoading) return <Loading />;
  if (!data.length) return <EmptyState icon={Receipt} title="Nenhum boleto" />;
  return (
    <Section>
      {data.map((b) => (
        <div key={b.id} data-testid="bill-item" className={`glass rounded-3xl p-4 card-hover ${focusId === b.id ? "ring-2 ring-primary/60" : ""}`}>
          <div className="flex items-center justify-between">
            <div><p className="font-display font-semibold">{b.month}</p><p className="text-xs text-muted-foreground">Vencimento {new Date(b.due_date).toLocaleDateString("pt-BR")}</p></div>
            <span className={`text-[11px] px-2.5 py-1 rounded-full font-medium capitalize ${STATUS[b.status]}`}>{b.status}</span>
          </div>
          <p className="text-2xl font-display font-bold mt-2">{brl(b.amount)}</p>
          {b.status !== "pago" && (
            <div className="flex gap-2 mt-3">
              <button onClick={() => copy(b.code)} data-testid="bill-copy" className="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-white/5 border border-white/10 py-2 text-xs font-medium hover:bg-white/10 transition-colors"><Copy className="w-3.5 h-3.5" /> Copiar código</button>
              <button onClick={() => toast.message("Download disponível em breve.")} className="flex-1 flex items-center justify-center gap-1.5 rounded-full brand-gradient py-2 text-xs font-medium text-white"><Download className="w-3.5 h-3.5" /> Baixar</button>
            </div>
          )}
        </div>
      ))}
    </Section>
  );
}

function Balancete() {
  const { data, isLoading } = useQuery({ queryKey: ["financials"], queryFn: async () => (await api.get("/financials")).data });
  if (isLoading) return <Loading />;
  if (!data.length) return <EmptyState icon={PieIcon} title="Sem balancete" />;
  const f = data[0];
  const pieData = Object.entries(f.categories).map(([name, value]) => ({ name, value }));
  return (
    <Section>
      <div className="glass rounded-3xl p-5">
        <p className="text-sm font-semibold mb-1">{f.month}</p>
        <div className="grid grid-cols-3 gap-3 mt-3">
          <div><p className="text-xs text-muted-foreground">Receitas</p><p className="font-display font-bold text-emerald-400 text-sm">{brl(f.revenue)}</p></div>
          <div><p className="text-xs text-muted-foreground">Despesas</p><p className="font-display font-bold text-red-400 text-sm">{brl(f.expenses)}</p></div>
          <div><p className="text-xs text-muted-foreground">Saldo</p><p className="font-display font-bold text-primary text-sm">{brl(f.balance)}</p></div>
        </div>
      </div>
      <div className="glass rounded-3xl p-5">
        <p className="text-sm font-semibold mb-3">Despesas por categoria</p>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={3}>
                {pieData.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#17171C", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} formatter={(v) => brl(v)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3">
          {pieData.map((e, i) => (
            <div key={e.name} className="flex items-center gap-2 text-xs">
              <span className="w-3 h-3 rounded-sm" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
              <span className="flex-1 text-muted-foreground">{e.name}</span>
              <span className="font-medium">{brl(e.value)}</span>
            </div>
          ))}
        </div>
      </div>
      {f.budget && (
        <div className="glass rounded-3xl p-5">
          <p className="text-sm font-semibold mb-3">Orçado x Realizado</p>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[{ name: f.month, Orçado: f.budget, Realizado: f.expenses }]}>
                <XAxis dataKey="name" tick={{ fill: "#71717A", fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#17171C", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} formatter={(v) => brl(v)} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                <Bar dataKey="Orçado" fill="#8A2BE2" radius={[8, 8, 0, 0]} />
                <Bar dataKey="Realizado" fill="#FF2079" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </Section>
  );
}

function Multas({ focusId }) {
  const { data, isLoading } = useQuery({ queryKey: ["fines"], queryFn: async () => (await api.get("/fines")).data });
  if (isLoading) return <Loading />;
  if (!data.length) return <EmptyState icon={AlertTriangle} title="Nenhuma multa ou advertência" subtitle="Você está em dia. 🎉" />;
  return (
    <Section>
      {data.map((f) => (
        <div key={f.id} data-testid="fine-item" className={`glass rounded-3xl p-4 card-hover ${focusId === f.id ? "ring-2 ring-primary/60" : ""}`}>
          <div className="flex items-center justify-between">
            <span className="text-[11px] px-2.5 py-1 rounded-full bg-yellow-500/10 text-yellow-400 font-medium">{f.type}</span>
            <span className="text-xs text-muted-foreground capitalize">{f.status}</span>
          </div>
          <p className="text-sm mt-2">{f.reason}</p>
          <p className="text-xs text-muted-foreground mt-1">{new Date(f.date).toLocaleDateString("pt-BR")}</p>
        </div>
      ))}
    </Section>
  );
}

function Certidoes() {
  const { data, isLoading } = useQuery({ queryKey: ["certificates"], queryFn: async () => (await api.get("/certificates")).data });
  if (isLoading) return <Loading />;
  if (!data.length) return <EmptyState icon={Award} title="Nenhuma certidão" />;
  return (
    <Section>
      {data.map((c) => (
        <div key={c.id} data-testid="certificate-item" className="glass rounded-2xl p-4 flex items-center gap-3 card-hover">
          <Award className="w-5 h-5 text-primary" />
          <p className="flex-1 text-sm font-semibold">{c.title}</p>
          <Download className="w-4 h-4 text-muted-foreground" />
        </div>
      ))}
    </Section>
  );
}

const VIEWS = {
  encomendas: Encomendas, reservas: Reservas, enquetes: Enquetes, assembleias: Assembleias,
  boletos: Boletos, balancete: Balancete, multas: Multas, certidoes: Certidoes,
  regimento: () => <PublicationsView type="regimento" label="Regimento Interno" />,
  manuais: () => <PublicationsView type="manual" label="Manuais" />,
};

export default function Services() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const catParam = params.get("cat");
  const pkgParam = params.get("pkg");
  const [active, setActive] = useState(catParam || null);
  const [seenTick, setSeenTick] = useState(0);
  const { data: summary } = useQuery({ queryKey: ["services-summary"], queryFn: async () => (await api.get("/services/summary")).data, refetchInterval: 30000 });
  useEffect(() => { if (catParam) setActive(catParam); }, [catParam]);
  const View = active ? VIEWS[active] : null;

  const isNew = (key) => {
    const sig = summary?.[key];
    if (!sig || sig === "0") return false;
    return localStorage.getItem(`konddo_seen_${key}`) !== String(sig);
  };
  const openTile = (key) => {
    if (summary?.[key] != null) localStorage.setItem(`konddo_seen_${key}`, String(summary[key]));
    setSeenTick((t) => t + 1);
    setActive(key);
  };
  const goBack = () => { setActive(null); setParams({}); };
  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["services-summary"] }),
      qc.invalidateQueries({ queryKey: ["packages"] }),
      qc.invalidateQueries({ queryKey: ["polls"] }),
      qc.invalidateQueries({ queryKey: ["bills"] }),
      qc.invalidateQueries({ queryKey: ["fines"] }),
    ]);
  };

  if (active) {
    const cat = CATS.find((c) => c.key === active);
    return (
      <PullToRefresh onRefresh={refresh}>
        <div className="space-y-4">
          <button onClick={goBack} data-testid="services-back" className="text-sm text-muted-foreground hover:text-foreground transition-colors">← Serviços</button>
          <h1 className="font-display font-bold text-2xl tracking-tight flex items-center gap-2"><cat.icon className="w-6 h-6 text-primary" /> {cat.label}</h1>
          <View pkgId={active === "encomendas" ? pkgParam : undefined} focusId={params.get("focus") || undefined} />
        </div>
      </PullToRefresh>
    );
  }

  return (
    <PullToRefresh onRefresh={refresh}>
      <div className="space-y-5" data-seen={seenTick}>
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight">Serviços</h1>
          <p className="text-sm text-muted-foreground">Tudo do seu condomínio, sem burocracia.</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {CATS.map((c) => (
            <button key={c.key} onClick={() => openTile(c.key)} data-testid={`service-${c.key}`} className="relative glass rounded-3xl p-4 flex flex-col items-center gap-2 card-hover aspect-square justify-center">
              {isNew(c.key) && <span data-testid={`service-dot-${c.key}`} className="absolute top-3 right-3 w-2.5 h-2.5 rounded-full brand-gradient ring-2 ring-background" />}
              <div className="w-11 h-11 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center"><c.icon className="w-5 h-5 text-primary" /></div>
              <p className="text-xs font-medium text-center">{c.label}</p>
            </button>
          ))}
        </div>
      </div>
    </PullToRefresh>
  );
}
